import React, { useEffect, useState, useRef } from 'react'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	CircularProgress,
	Box,
	Tabs,
	Tab,
	Alert,
} from '@mui/material'
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material'
import { FeedbackGridApi } from '../api/feedbackGridApi'
import { CategoryTreeSelectWrapper } from './CategoryTreeSelectWrapper'
import type { Id, Sentiment, CategoryChangeData } from '../../treeSelect/types'
import type { CategoryData } from '../utils/mermaidBuilder'

interface CategoryEditDialogProps {
	feedbackId: number
	open: boolean
	onClose: () => void
	onSave: (
		categoryData?: Map<number, CategoryData[]>,
		updatedValues?: {
			annotated: boolean
			checked: boolean
			sentiment: number | null
			sentiment_color: string | null
			sentiment_label: string | null
		},
	) => void
	api: FeedbackGridApi
	translations: Record<string, string>
}

interface CategoryEditData {
	questions: Array<{ id: number; name: string }>
	currentCategories: Record<string, Record<number, number | null>>
	editAllowed: boolean
	isUserCategoryManager: boolean
	treeSelectEndpoint: string
	preloadedTreeData?: Record<number, { data: Record<string, any[]>; active: number[] }>
}

interface TabPanelProps {
	children?: React.ReactNode
	index: number
	value: number
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`category-tabpanel-${index}`}
			aria-labelledby={`category-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 1 }}>{children}</Box>}
		</div>
	)
}

/**
 * Dialog for editing Text Analytics categories with sentiment
 *
 * Uses the MultiTreeSelect React component directly
 */
export const CategoryEditDialog: React.FC<CategoryEditDialogProps> = ({
	feedbackId,
	open,
	onClose,
	onSave,
	api,
	translations = {},
}) => {
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [data, setData] = useState<CategoryEditData | null>(null)
	const [currentTab, setCurrentTab] = useState(0)

	// Track category changes from tree selects
	// Map of questionId -> CategoryChangeData
	const categoryChangesRef = useRef<Map<number, CategoryChangeData>>(new Map())

	// Load category edit data when dialog opens
	useEffect(() => {
		if (!open) {
			setData(null)
			setLoading(true)
			setError(null)
			setCurrentTab(0)
			categoryChangesRef.current.clear()
			return
		}

		const loadData = async () => {
			setLoading(true)
			setError(null)

			try {
				const response = await api.getCategoryEditData(feedbackId)
				if (response.success && response.data) {
					setData(response.data)
				} else {
					setError(response.error || 'Failed to load category data')
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load category data')
			} finally {
				setLoading(false)
			}
		}

		loadData()
	}, [open, feedbackId, api])

	// Handle category changes from tree selects
	const handleCategoryChange = (questionId: number, changeData: CategoryChangeData) => {
		categoryChangesRef.current.set(questionId, changeData)
	}

	const handleSave = async () => {
		if (!data) return

		setSaving(true)
		setError(null)

		try {
			// Collect category data from hidden inputs that MultiTreeSelect populates
			const categories: Record<string, Record<number, number | null>> = {}

			data.questions.forEach((question) => {
				const questionId = question.id
				const inputId = `categories_${questionId}`
				const hiddenInput = document.getElementById(inputId) as HTMLInputElement | null

				if (!hiddenInput || !hiddenInput.value) {
					categories[questionId.toString()] = {}
					return
				}

				try {
					// Parse the JSON data: [{value: categoryId, sentiment: sentimentValue}, ...]
					const selectedData = JSON.parse(hiddenInput.value)

					// Convert array format to object format: {categoryId: sentiment}
					const categoriesForQuestion: Record<number, number | null> = {}
					if (Array.isArray(selectedData)) {
						selectedData.forEach((item: { value: number; sentiment: number | null }) => {
							categoriesForQuestion[item.value] = item.sentiment
						})
					}

					categories[questionId.toString()] = categoriesForQuestion
				} catch (e) {
					console.error('Failed to parse category data', e)
					categories[questionId.toString()] = {}
				}
			})

			const response = await api.saveCategoryEdit(feedbackId, categories)

			if (response.success) {
				// Build category data for mermaid diagram from tracked changes
				const categoryDataForMermaid = new Map<number, CategoryData[]>()

				categoryChangesRef.current.forEach((changeData, questionId) => {
					const categoriesForQuestion: CategoryData[] = []

					// changeData.categories now includes both selected categories AND their parents
					// Convert all categories to CategoryData format
					Object.entries(changeData.categories).forEach(([catIdStr, catInfo]) => {
						categoriesForQuestion.push({
							id: Number(catIdStr),
							name: catInfo.name,
							parent: catInfo.parentId !== null ? Number(catInfo.parentId) : null,
							sentiment: catInfo.sentiment,
						})
					})

					if (categoriesForQuestion.length > 0) {
						categoryDataForMermaid.set(questionId, categoriesForQuestion)
					}
				})

				onSave(categoryDataForMermaid, response.data)
			} else {
				setError(response.error || 'Failed to save categories')
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save categories')
		} finally {
			setSaving(false)
		}
	}

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue)
	}

	if (!open) {
		return null
	}

	if (data && (!data.editAllowed || !data.isUserCategoryManager)) {
		return (
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle>{translations.editCategories || 'Edit Categories'}</DialogTitle>
				<DialogContent>
					<Alert severity="warning">
						{translations.noPermissionToEdit || 'You do not have permission to edit categories'}
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} className="!bg-primary" variant="contained" size="small" startIcon={<CloseIcon />}>
						{translations.close || 'Close'}
					</Button>
				</DialogActions>
			</Dialog>
		)
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullWidth
			PaperProps={{
				sx: {
					overflow: 'visible',
					width: '80%',
					maxWidth: 'none',
					m: 0,
					position: 'absolute',
					top: '20px',
				},
			}}
			disableScrollLock={false}
			hideBackdrop={false}
			onTransitionExited={() => {
				// Safety check to ensure scroll is restored after transition
				document.body.style.overflow = ''
				document.documentElement.style.overflow = ''
			}}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
				<DialogTitle sx={{ py: 1, fontSize: '1rem' }}>{translations.editCategories || 'Edit Categories'}</DialogTitle>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			<DialogContent sx={{ p: 1, overflow: 'visible' }}>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
						<CircularProgress />
					</Box>
				)}

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{!loading && data && data.questions.length > 0 && (
					<Box sx={{ width: '100%' }}>
						{data.questions.length > 1 && (
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								aria-label="category questions"
								variant="scrollable"
								scrollButtons="auto"
								sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
							>
								{data.questions.map((question, index) => (
									<Tab
										key={question.id}
										label={question.name || `Q ${question.id === 0 ? 'M' : question.id}`}
										id={`category-tab-${index}`}
										aria-controls={`category-tabpanel-${index}`}
									/>
								))}
							</Tabs>
						)}

						{data.questions.map((question, index) => {
							const questionId = question.id
							return (
								<TabPanel key={questionId} value={currentTab} index={index}>
									<Box sx={{ minHeight: '100px' }}>
										<CategoryTreeSelectWrapper
											feedbackId={feedbackId}
											questionId={questionId}
											currentCategories={data.currentCategories[questionId.toString()] || {}}
											preloadedTreeData={data.preloadedTreeData?.[questionId]?.data || null}
											preloadedActive={data.preloadedTreeData?.[questionId]?.active || null}
											treeSelectEndpoint={data.treeSelectEndpoint}
											leafOnly={true}
											onChange={handleCategoryChange}
											translations={translations}
										/>
									</Box>
								</TabPanel>
							)
						})}
					</Box>
				)}

				{!loading && data && data.questions.length === 0 && (
					<Alert severity="info">{translations.noQuestionsAvailable || 'No questions available'}</Alert>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 1, pt: 0 }}>
				<Button
					onClick={handleSave}
					variant="contained"
					className="!bg-primary"
					size="small"
					startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
					disabled={loading || saving || !data}
					fullWidth
				>
					{translations.save || 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
